import { useCallback, useMemo } from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { isMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isStandalone } from '../../../constants';
import { processNeedRedirectWindowToFallback } from '../../../widget/helpers';
import { useGenerateVCard } from './useGenerateVCard';

const DEFAULT_FEATURES =
  'width=800px,height=700px,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';

const navigateToLink = (url: string) => {
  window.location.href = url;
};

interface UseButtonOpenLinkProps {
  title?: string;
  url?: string;
  fallbackUrl?: string;
  options?: {
    withFeatures?: boolean;
    openCurrentPage?: boolean;
    target: string;
  };
  vcardSettings?: {
    name: string;
    job: string;
    company: string;
    image: string;
    phone: string;
    address: string;
    email: string;
    website: string;
  } | null;
}

export const useButtonOpenLink = ({
  title,
  url,
  fallbackUrl,
  options,
  vcardSettings = null
}: UseButtonOpenLinkProps) => {
  const { googleAnalytics, widgetId, editMode } = useSettings();
  const {
    target = '_blank',
    withFeatures = true,
    openCurrentPage = false
  } = options ?? {};
  const actualTarget = editMode ? '_blank' : target;
  const isOpenLinkOnModal = withFeatures && !isMobile();
  const vcardLink = useGenerateVCard(vcardSettings);

  const link = useMemo(() => {
    if (!url && vcardSettings) {
      return vcardLink;
    }

    return url;
  }, [url, vcardSettings, vcardLink]);

  const openLinkInNewWindow = useCallback(
    (url: string, features: string, fallbackUrl?: string) => {
      const opener = window.open(
        url,
        isOpenLinkOnModal ? title : actualTarget,
        features
      );

      if (fallbackUrl && opener) {
        processNeedRedirectWindowToFallback(opener, url, fallbackUrl);
      }
    },
    [actualTarget, isOpenLinkOnModal, title]
  );

  return useCallback(() => {
    if (!link) return;

    sendGoogleAnalyticsEvents({
      googleAnalyticsEvents: googleAnalytics,
      eventType: isStandalone ? 'chatStarted' : 'buttonClicked',
      valueMapping: {
        widgetId,
        buttonType: title
      }
    });

    if (openCurrentPage) {
      return navigateToLink(link);
    }

    const features = isOpenLinkOnModal ? DEFAULT_FEATURES : '';

    return openLinkInNewWindow(link, features, fallbackUrl);
  }, [
    link,
    openCurrentPage,
    isOpenLinkOnModal,
    openLinkInNewWindow,
    fallbackUrl,
    title,
    googleAnalytics,
    widgetId
  ]);
};
